import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Mail, Send, AlertTriangle, Eye, EyeOff, Loader2, Bell, Gift, Share2, Printer,
  RefreshCw, Building, CreditCard, Megaphone, Users, CheckCircle, XCircle, Save,
} from 'lucide-react';

/**
 * Tab "firma" – ausgelagerte Tab-Komponente.
 * Erhält alle benötigten State- und Handler-Referenzen vom Parent via Props.
 */
const SettingsFirma = ({ ctx }) => {
  const { t } = useTranslation();
  const {
    settings, setSettings, handleInputChange,
    showSmtpPassword, setShowSmtpPassword,
    testEmailTo, setTestEmailTo, sendingTest, testResult, handleTestEmail,
    triggeringFollowUp, followUpResult, handleTriggerFollowUp,
    triggeringBackup, backupResult, handleTriggerBackup,
    testingQrBill, qrBillResult, handleTestQrBill,
  } = ctx;

  return (
    <>
          <Card className="card-shadow mb-6" data-testid="settings-invoice-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Rechnungs-Absender &amp; Swiss QR-Rechnung
              </CardTitle>
              <CardDescription>
                Diese Angaben werden auf jeder Rechnung als Absender ausgedruckt und in den Zahlteil der Swiss QR-Rechnung übernommen.
                Strukturierte Adresse (getrennt Strasse / Nr. / PLZ / Ort) ist Pflicht seit SIX v2.3.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="company_name">Firmenname</Label>
                  <Input id="company_name" name="company_name"
                    value={settings.company_name} onChange={handleInputChange}
                    placeholder="ZO Werbeflächen"
                    data-testid="settings-company-name-input" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="company_street">Strasse</Label>
                  <Input id="company_street" name="company_street"
                    value={settings.company_street} onChange={handleInputChange}
                    placeholder="Bahnhofstrasse"
                    data-testid="settings-company-street-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_house_num">Hausnummer</Label>
                  <Input id="company_house_num" name="company_house_num"
                    value={settings.company_house_num} onChange={handleInputChange}
                    placeholder="75"
                    data-testid="settings-company-house-num-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_pcode">PLZ</Label>
                  <Input id="company_pcode" name="company_pcode"
                    value={settings.company_pcode} onChange={handleInputChange}
                    placeholder="8620"
                    data-testid="settings-company-pcode-input" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="company_city">Ort</Label>
                  <Input id="company_city" name="company_city"
                    value={settings.company_city} onChange={handleInputChange}
                    placeholder="Wetzikon"
                    data-testid="settings-company-city-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_country">Land (ISO)</Label>
                  <Input id="company_country" name="company_country"
                    value={settings.company_country} onChange={handleInputChange}
                    placeholder="CH" maxLength={2}
                    className="uppercase"
                    data-testid="settings-company-country-input" />
                </div>
              </div>

              <div className="border-t pt-4 mt-2 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_iban">IBAN (normale IBAN, ohne Referenz)</Label>
                  <Input id="company_iban" name="company_iban"
                    value={settings.company_iban} onChange={handleInputChange}
                    placeholder="CH93 0076 2011 6238 5295 7"
                    className="font-mono text-sm"
                    data-testid="settings-company-iban-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Wird verwendet, wenn keine QR-IBAN gepflegt ist. Der QR-Slip enthält dann keine strukturierte Referenz („NON").
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_qr_iban">QR-IBAN (IID 30000–31999, mit QRR-Referenz)</Label>
                  <Input id="company_qr_iban" name="company_qr_iban"
                    value={settings.company_qr_iban} onChange={handleInputChange}
                    placeholder="CH44 3199 9123 0008 8901 2"
                    className="font-mono text-sm"
                    data-testid="settings-company-qr-iban-input" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Falls gesetzt, wird sie bevorzugt verwendet und die Rechnungsnummer automatisch als 27-stellige QRR-Referenz codiert.
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 flex items-center gap-3">
                <Button type="button" variant="outline"
                  onClick={handleTestQrBill}
                  disabled={testingQrBill}
                  data-testid="settings-qr-bill-test-btn">
                  {testingQrBill ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Muster-QR-Rechnung herunterladen
                </Button>
                {qrBillResult && (
                  <Alert variant={qrBillResult.type === 'success' ? 'default' : 'destructive'} className="flex-1 py-2">
                    <AlertDescription className="text-xs">{qrBillResult.message}</AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle>Google Maps Integration</CardTitle>
              <CardDescription>
                Hinterlegen Sie Ihren Google Maps API Key, um die Kartenansicht zu aktivieren.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="google_maps_api_key">{t('admin.googleMapsKey')}</Label>
                <Input
                  id="google_maps_api_key"
                  name="google_maps_api_key"
                  value={settings.google_maps_api_key}
                  onChange={handleInputChange}
                  placeholder="AIzaSy..."
                  className="font-mono text-sm"
                  data-testid="settings-google-maps-key-input"
                />
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Erhalten Sie einen API Key unter{' '}
                  <a
                    href="https://console.cloud.google.com/apis/credentials"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[hsl(var(--primary))] hover:underline"
                  >
                    Google Cloud Console
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Google Search Console */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle>Google Search Console</CardTitle>
              <CardDescription>
                Verifizieren Sie Ihre Website bei Google, um die Indexierung zu überwachen und die Sitemap einzureichen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Label htmlFor="google_verification_code">Verifizierungscode</Label>
                <Input
                  id="google_verification_code"
                  name="google_verification_code"
                  value={settings.google_verification_code}
                  onChange={handleInputChange}
                  placeholder="z.B. abc123def456..."
                  className="font-mono text-sm"
                  data-testid="settings-google-verification-input"
                />
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Den Code finden Sie in der{' '}
                  <a
                    href="https://search.google.com/search-console"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[hsl(var(--primary))] hover:underline"
                  >
                    Google Search Console
                  </a>
                  {' '}unter "Einstellungen &gt; Inhaberschaft bestätigen &gt; HTML-Datei".
                </p>
                <div className="bg-[hsl(var(--surface-2))] rounded-lg p-4 text-sm space-y-2">
                  <p className="font-semibold text-[hsl(var(--foreground))]">Anleitung:</p>
                  <ol className="list-decimal pl-5 space-y-1 text-[hsl(var(--muted-foreground))]">
                    <li>Gehen Sie zu <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">search.google.com/search-console</a></li>
                    <li>Klicken Sie auf "Property hinzufügen" → "URL-Präfix"</li>
                    <li>Geben Sie Ihre Domain ein (z.B. https://zo-werbefläche.ch)</li>
                    <li>Wählen Sie "HTML-Datei" als Verifizierungsmethode</li>
                    <li>Kopieren Sie den Code aus dem Dateinamen (z.B. google<strong>abc123</strong>.html → Code ist <strong>abc123</strong>)</li>
                    <li>Fügen Sie den Code oben ein und speichern Sie</li>
                    <li>Klicken Sie in der Search Console auf "Bestätigen"</li>
                    <li>Nach Verifizierung: Gehen Sie zu "Sitemaps" und reichen Sie <code>/api/sitemap.xml</code> ein</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
    </>
  );
};

export default SettingsFirma;
